import React, { useEffect, useState } from 'react';
import moment from 'moment';
import {
  Form,
  Input,
  Button,
  Layout,
  notification,
  Tooltip,
  Avatar
} from 'antd';
import { Comment } from '@ant-design/compatible';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  UserOutlined,
  LockOutlined,
} from '@ant-design/icons';
import {
  useSetRecoilState,
  useRecoilValueLoadable,
} from 'recoil';
import { configSelector } from '../../../atoms/Selectors';
import {
  profileAtom,
  tokenAtom,
  stateAtom
} from '../../../atoms/Atoms';
import envConfig from '../../../envConfig';
import api from '../../../api/api';
import LanguageSwitcher from '../LanguageSwitcher';
import Grocefy from '../../../assets/images/grocefyLogoAlone.png';

function validateAdminRole(roleObject) {
  const isValid =
    roleObject.includes('Admin') ||
    roleObject.includes('DeliveryProvider') ||
    roleObject.includes('Marketing') ||
    roleObject.includes('CustomerService') ||
    roleObject.includes('CustomerSupport') ||
    roleObject.includes('Finance') ||
    roleObject.includes('StoreManager') ||
    roleObject.includes('StoreAdmin') ||
    roleObject.includes('CustomerServiceAdmin') ||
    roleObject.includes('CustomerSupportAdmin') ||
    roleObject.includes('SuperAdmin');
  if (isValid) {
    return true;
  }
  return false;
}

function Home() {
  const config = useRecoilValueLoadable(configSelector);
  const { i18n, t } = useTranslation();
  const history = useHistory();

  const [submitting, setSubmitting] = useState(false);
  const setProfile = useSetRecoilState(profileAtom);
  const setState = useSetRecoilState(stateAtom);
  const setToken = useSetRecoilState(tokenAtom);
  const [allowSubmit, setAllowSubmit] = useState(false);
  const [data, setData] = useState({
    email: '',
    password: '',
    emailValid: false,
    passwordValid: false,
  });
  const [language, setLanguage] = useState(i18n.language);

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function changeLanguage(lang) {
    setLanguage(lang);
    i18n.changeLanguage(lang).then(() => {
      window.localStorage.setItem(
        `${envConfig.REACT_APP_PREFIX}-admin-lang`,
        JSON.stringify(lang)
      );
    });
  }

  const submit = () => {
    setSubmitting(true);
    const json = JSON.stringify({
      username: data.email,
      password: data.password,
    });
    api
      .post('oauth/local/login', json, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then((response) => {
        setSubmitting(false);
        if (response.status === 200 && response.data.error) {
          showMessage(response.data.error);
        } else if (response.status === 200) {
          const expiration = moment().add(1000000000, 'seconds');
          setToken(response.data.data.accessToken);
          const allow = validateAdminRole(
            JSON.stringify(response.data.data.state.profile.roles)
          );
          if (allow) {
            localStorage.setItem(
              `${envConfig.REACT_APP_PREFIX}-admin-token`,
              response.data.data.accessToken
            );
            localStorage.setItem(
              `${envConfig.REACT_APP_PREFIX}-admin-currentstate`,
              JSON.stringify(response.data.data.state)
            );
            localStorage.setItem(
              `${envConfig.REACT_APP_PREFIX}-admin-expire`,
              expiration
            );
            localStorage.setItem(
              `${envConfig.REACT_APP_PREFIX}-admin-profile`,
              JSON.stringify(response.data.data.state.profile)
            );
            setProfile(response.data.data.state.profile);
            // console.log('Setting default language to: ', `${envConfig.REACT_APP_DEFAULT_LANGUAGE}`);
            changeLanguage(`${envConfig.REACT_APP_DEFAULT_LANGUAGE}`);
            setState(response.data.data.state);
          } else {
            localStorage.removeItem(
              `${envConfig.REACT_APP_PREFIX}-admin-currentstate`
            );
            localStorage.removeItem(
              `${envConfig.REACT_APP_PREFIX}-admin-token`
            );
            localStorage.removeItem(
              `${envConfig.REACT_APP_PREFIX}-admin-expire`
            );
            localStorage.removeItem(
              `${envConfig.REACT_APP_PREFIX}-admin-profile`
            );
            setToken(null);
            showMessage(t('not_authorized_on_platform'));
          }
        }
      })
      .catch((error) => {
        console.error(error);
        setSubmitting(false);
      });
  };

  useEffect(() => {
    setLanguage(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    if (data) {
      let valid = true;
      valid = valid && data.emailValid === 'warning';
      valid = valid && data.passwordValid === 'warning';
      setAllowSubmit(valid);
    }
  }, [data]);

  return (
    <div>
      <Layout.Header theme="dark" className="h-auto">
        <div className="flex flex-col items-center md:flex-row">
          <div className="mt-6 md:my-2">
            {config.state === 'hasValue' && (
              <img
                alt="Store Logo"
                className="md:ml-3 h-6"
                src={
                  config?.state === 'hasValue' &&
                  config?.contents?.data?.adminHeaderLogoCombo
                }
              />
            )}
          </div>
          <LanguageSwitcher small className="ml-4" />
          <Layout className="flex items-end bg-transparent pt-8">
            <Layout.Content>
              <div className="lg:flex">
                <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2 mb-2">
                  <Input
                    status={data.emailValid}
                    disabled={submitting}
                    placeholder={t('email')}
                    onKeyDown={(key) => {
                      if (!submitting && allowSubmit && key.key === 'Enter') {
                        submit();
                      }
                    }}
                    onChange={(obj) => {
                      const pattern = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(.\w{2,3})+$/;
                      setData({
                        ...data,
                        email: obj.target.value,
                        emailValid: obj.target.value.trim().length > 0 && pattern.test(obj.target.value.trim()) ? 'warning' : 'error'
                      });
                    }}
                    value={data.email}
                  />
                  <Input.Password
                    status={data.passwordValid}
                    disabled={submitting}
                    onKeyDown={(key) => {
                      if (!submitting && allowSubmit && key.key === 'Enter') {
                        submit();
                      }
                    }}
                    placeholder={t('password')}
                    onChange={(obj) => setData({
                      ...data,
                      password: obj.target.value,
                      passwordValid: obj.target.value.trim().length > 0 ? 'warning' : 'error',
                      confirmPasswordValid:
                        obj.target.value.trim().length > 0 ? 'warning' : 'error'
                    })}
                    value={data.password}
                  />
                  <Button
                    className="login-form-button"
                    loading={submitting}
                    disabled={submitting || !allowSubmit}
                    onClick={() => submit()}
                    type="primary"
                  >
                    {t('login')}
                  </Button>
                  <Button
                    type="primary"
                    className="login-form-button"
                    onClick={() => history.push('/register')}
                  >
                    {t('register')}
                  </Button>
                </div>
                <a href="/forgotpass" className="text-white h-6 lg:-mt-4 lg:ml-2">{t('forgot_password')}</a>
              </div>
              {/* <Form
                className="flex flex-col md:flex-row"
                name={t('login')}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
              >
                <Form.Item
                  className="md:mr-2"
                  name="username"
                  rules={[
                    {
                      required: true,
                      message: t('email_input'),
                      className: 'text-white',
                    },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="site-form-item-icon" />}
                    value={username}
                    onChange={(obj) => setUsername(obj.target.value)}
                    placeholder={t('username')}
                  />
                </Form.Item>
                <Form.Item
                  className="md:mr-2 text-white"
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: t('password_description'),
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined className="site-form-item-icon" />}
                    value={password}
                    onChange={(obj) => setPassword(obj.target.value)}
                    placeholder={t('password')}
                  />
                  <Link to="/forgotpass">
                    <p>{t('forgot_password')}</p>
                  </Link>
                </Form.Item>
                <div className="flex flex-row justify-center">
                  <Form.Item>
                    <Button
                      loading={submitting}
                      type="primary"
                      htmlType="submit"
                      className="login-form-button md:ml-4"
                    >
                      {t('login')}
                    </Button>
                  </Form.Item>
                  <Link to="/register">
                    <Button
                      type="primary"
                      className="login-form-button md:ml-4 ml-12"
                    >
                      {t('register')}
                    </Button>
                  </Link>
                </div>
              </Form>
              {err && (<div className="text-white text-xs py-0" style={{ marginTop: '-45px' }}>{err}</div>)} */}
            </Layout.Content>
          </Layout>
        </div>
      </Layout.Header>
    </div>
  );
}

export default Home;
